import { ref } from 'vue'

import router from '@/router'
import { useAppStore, useCardStore, useProcessStore, useWebviewStore } from '@/store'

import useNative from '../useNative'
import useBindCard from './useBindCard'
import useFindPwd from './useFindPwd'
import { ServerError } from '@/model/serverError'

import type { PayPreOrderPwdParams } from '@/api/pay'

import {
  getFirstCardPhone,
  payPreOrderPwd,
  queryPreOrderByTradeNo,
  vmPay3DSAuthentication,
} from '@/api/pay'
import { unBindCardChangeDevice, unBindCardNoPwd } from '@/api/unBindCard'

import { showDialog, showConfirmDialog, showSuccessToast } from 'vant'
import Spinner from '@/components/Spinner'

import { useI18n } from '@/hooks/useI18n'

import { encryptPwdAndSetTimestamp } from '@/utils/crypto'
import handleHttpError from '@/utils/fetch/handleHttpError'

import { PAY_STATUS, PROCESS } from '@/constant'

export default function usePay() {
  const appStore = useAppStore()
  const cardStore = useCardStore()
  const processStore = useProcessStore()
  const webviewStore = useWebviewStore()

  const { t } = useI18n()

  const password = ref('')
  const isShowPwdPopup = ref(false)

  const orderInfo = ref({} as OrderInfo)
  const cardList = ref([] as CardInfo[])
  const selectedCard = ref({} as CardInfo)
  const selectedCardId = ref('')

  const { exit } = useNative()

  /** 查询预订单 */
  const queryPreOrder = () => {
    const { tradeNo } = appStore
    console.log('[查询预订单的订单号] ', tradeNo)

    // 没有订单号 ? (澳觅APP)就生成订单 : 有订单号就查询订单
    const result = !tradeNo ? appStore.generateOrder() : queryPreOrderByTradeNo(tradeNo)

    result.then((cardListRes: any) => {
      // 预订单信息
      orderInfo.value = cardListRes?.result?.queryPrePayOrderVo || {}
      // 卡列表
      cardList.value = cardListRes?.result?.customerCardsVoList || []

      // 默认卡是第一张可用的卡
      if (cardList.value.length > 0) {
        cardList.value.every((item) => {
          if (item.available) {
            selectedCard.value = item
            selectedCardId.value = item.cardId
            return false
          }
          return true
        })
      } else {
        selectedCardId.value = ''
      }

      Spinner.close()

      // 进入卡列表 如果已绑卡但未设置密码 需要进行解绑卡重新绑卡 会自动解绑
      if (cardList.value.length && !appStore.hadSetPwd) {
        unBindCardToBindNewCard()
      }
    })
  }

  /**
   * 支付预订单
   * @param cardId
   * @param password
   * @param discountInfo
   * @param smsCode - 验证码 支付的时候需要验证码使用
   */
  const payPreOrder = (cardId, passwords, discountInfo, smsCode?: string) => {
    password.value = ''

    const params: PayPreOrderPwdParams = {
      cardId,
      password: passwords,
      smsCode,
      tradeNo: appStore.tradeNo,
      discountType: discountInfo.discountType,
      discount: discountInfo.discountAmount,
      discountActId: discountInfo.discountActId,
    }
    // return;

    Spinner.loading()

    payPreOrderPwd(encryptPwdAndSetTimestamp(params))
      .then((res) => {
        const body = res.result

        if (smsCode) {
          // 结束流程
        }

        const tradeNo = body.tradeNo

        if (body.tradeStatus === PAY_STATUS.TRADE_CLOSE_TIMEOUT) {
          // 交易关闭 通知客户端
          alertAndExitSDK({ content: '交易關閉' })
          // 这个时候如果不退出 执行下去支付密码接口会返回成功参数 并且不管你密码是否对错
          // 还会跳转到支付成果页显示了空白页面 因为没有订单金额
          return
        }

        showSuccessToast({
          message: '支付成功！',
          duration: 2500,
          teleport: '#wrapper',
        })

        Spinner.close()

        router.push(`payResult?tradeNo=${tradeNo}`)
      })
      .catch((error) => {
        console.log('[捕获支付错误] ', error)
        Spinner.close()
        handlePayError(error, params)
      })
  }

  /** 支付错误码处理 */
  function handlePayError(res, params?: any) {
    const error = new ServerError(res)

    if (error.code !== '400') return

    const handleMethodsMap = new Map([
      [['5012', '5018', '5040', '8062'], (error) => bindNewCard(error)],
      [['6014', '6015'], (error) => useOtherCard(error)],
      [['8024', '8025', '8026', '8040', '8077', '8100'], (error) => noQuota(error)],
      [
        ['6001', '6012', '6020', '5024', '6029', '6030', '7000'],
        (error) => alertAndResetPay(error),
      ],
      [['6004', '6017'], (error) => rePay(error)],
      [
        ['1016'],
        (error) => {
          showDialog({
            title: '支付失敗',
            message: error.content,
          }).then(() => {
            exit({
              tradeStatus: 6002,
              msg: '訂單超時',
            })
          })
        },
      ],
      [
        ['5008'],
        (error) => {
          queryPreOrder()
          alertAndResetPay(error)
        },
      ],
      [['1017'], (error) => pwdErrLock(error)],
      [['1018'], (error) => pwdErr(error)],
      [
        ['6023'],
        () => {
          toPaySmsAuth(params)
          password.value = ''
        },
      ],
      [
        ['6104'],
        () => {
          unBindCardToBindNewCardChangeDevice(params)
        },
      ],
      [
        ['6101'],
        () => {
          toPayVM3DS(params)
        },
      ],
    ])

    if (error.bCode) {
      let allCodeArr: string[] = []
      console.log('handleMethodsMap.keys(): ', handleMethodsMap.keys())
      for (const codeArr of handleMethodsMap.keys()) {
        allCodeArr = [...allCodeArr, ...codeArr]
        if (codeArr.includes(error.bCode)) {
          handleMethodsMap.get(codeArr)!(error)
        }
      }

      if (!allCodeArr.includes(error.bCode)) {
        // 除了上面特殊处理的错误码 其余走HTTP的错误码处理
        handleHttpError(res)
      }
    }
  }

  /** 提示并重置密码和关闭支付弹出框 */
  function alertAndResetPay(error) {
    showDialog({
      title: error.title || t('common.tipsText'),
      message: error.content,
    }).then(() => {
      password.value = ''
      isShowPwdPopup.value = false
    })
  }

  /**
   * 弹出提示框并退出SDK
   * @param error 错误内容
   * @param params 需要传递到设备的参数
   */
  function alertAndExitSDK(error, params?: any) {
    showDialog({
      title: error.title || t('common.tipsText'),
      message: error.content,
    }).then(() => {
      exit(params)
    })
  }

  /** 换卡支付 */
  function useOtherCard(error) {
    showConfirmDialog({
      title: t('common.tipsText'),
      message: error.content,
      confirmButtonText: '換卡支付',
      cancelButtonText: '取消',
      showCancelButton: true,
    })
      .then(() => {
        const backLength = window.history.length - appStore.historyStackLength
        router.go(-backLength)
      })
      .catch(() => {
        password.value = ''
        isShowPwdPopup.value = false
      })
  }

  /** 重新支付 */
  function rePay(error) {
    showConfirmDialog({
      title: '支付失敗',
      message: error.content,
      confirmButtonText: '重新支付',
      showCancelButton: false,
    }).then(() => {
      const backLength = window.history.length - appStore.historyStackLength
      router.go(-backLength)
    })
  }

  /** 无优惠 是否继续支付 */
  function noQuota(error) {
    showConfirmDialog({
      title: t('common.tipsText'),
      message: error.content,
      confirmButtonText: '繼續支付',
      cancelButtonText: '取消',
      showCancelButton: true,
    })
      .then(() => {
        selectedCard.value.discountActId = 'NODISCOUNT'
        payPreOrder(selectedCardId.value, password.value, selectedCard)
      })
      .catch(() => {
        queryPreOrder()
        password.value = ''
        isShowPwdPopup.value = false
      })
  }

  /** 支付密码错误 */
  function pwdErr(error) {
    // 密码错误(1018) 模板是 找回密码/重新输入
    // bCode: "1018"
    // bMsg: "支付密碼錯誤，请重试。"
    showConfirmDialog({
      title: '密碼錯誤',
      message: error.content,
      confirmButtonText: '重新輸入',
      cancelButtonText: '找回密碼',
      showCancelButton: true,
    })
      .then(() => {
        password.value = ''
        // params.value.passwordFocus();
        // popupVisible = true;
      })
      .catch(() => useFindPwd().toFindPayPwd())
  }

  /** 密码错误过多 账户锁定 */
  function pwdErrLock(error) {
    // 账户锁定(1017) 模板是 知道了
    // bCode: "1017"
    // bMsg: "{\"title\":\"賬號鎖定\",\"content\":\"支付密碼錯誤次數過多，您的賬號已被鎖定2小時。\"}"
    showDialog({
      title: error.title,
      message: error.content,
      confirmButtonText: t('common.iSeeText'),
    }).then(() => {
      isShowPwdPopup.value = false
      password.value = ''
      history.go(-1)
    })
  }

  /** 绑定新的卡 */
  function bindNewCard(error) {
    showConfirmDialog({
      title: error.title,
      message: error.content,
      confirmButtonText: '綁定新卡',
      cancelButtonText: '取消',
      showCancelButton: true,
    })
      .then(() => {
        const todo = processStore.todo
        useBindCard().toBindCard({
          todo,
          startPage: 'PayCardList',
        })
      })
      .catch(() => {
        password.value = ''
        isShowPwdPopup.value = false
      })
  }

  /** 设备更换 将解除原绑定卡片 需重新进行绑卡 */
  function unBindCardToBindNewCardChangeDevice(params) {
    showConfirmDialog({
      title: t('common.tipsText'),
      message: '由於您更換登錄設備，為了支付安全，系統將解除原綁定卡片，需您重新進行綁卡操作。',
      confirmButtonText: '綁定新卡',
      cancelButtonText: '取消',
      showCancelButton: true,
    })
      .then(() => {
        unBindCardChangeDevice({ cardId: params.cardId }).then(() => {
          const todo = processStore.todo

          useBindCard().toBindCard({
            todo,
            startPage: 'PayCardList',
            isSkipValidPwd: true,
          })
        })
      })
      .catch(() => {
        password.value = ''
        isShowPwdPopup.value = false
      })
  }

  /** 如果已绑卡但未设置密码 需要进行解绑卡重新绑卡 */
  function unBindCardToBindNewCard() {
    showDialog({
      title: t('common.tipsText'),
      message: '支付密碼未設置，請重新綁卡操作',
      confirmButtonText: t('common.iSeeText'),
      showCancelButton: false,
    }).then(async () => {
      Spinner.loading()
      await unBindCardNoPwd()
      Spinner.close()
      useBindCard().toBindCard({
        startPage: 'PayCardList',
      })
    })
  }

  /** 3DS支付 */
  function toPayVM3DS({ tradeNo }) {
    Spinner.loading()

    vmPay3DSAuthentication({ tradeNo })
      .then((res) => {
        const { thirdDSRedirectHtml, thirdDSState } = res.result

        cardStore.$patch({
          thirdDSState,
          thirdDSRedirectHtml,
        })

        Spinner.close()

        if (webviewStore.isFromMiniProgram) {
          showDialog({
            title: t('common.tipsText'),
            message: t('common.toAppTips'),
            confirmButtonText: t('common.iSeeText'),
            showCancelButton: false,
          }).then(() => {
            password.value = ''
            isShowPwdPopup.value = false
          })
          return
        }

        router.push('3ds')
      })
      .catch((error) => {
        console.log('[捕获支付错误] ', error)
        Spinner.close()
        handlePayError(error)
      })
  }

  /**
   * 快付短信验证
   * 1. 获取第一张卡的手机号 更新process数据
   * 2. 进入validateCode页面进行短信验证
   */
  function toPaySmsAuth(payParam) {
    // 添加支付流程（超额支付需要短信时添加）
    Spinner.loading()
    processStore.process === PROCESS.PAY
    processStore.setPage('payCardList')

    getFirstCardPhone().then((res) => {
      processStore.params.phone = res.result.phone
      processStore.params.payParam = payParam
      router.push('findPwdValidCode')
    })
  }

  return {
    password,
    isShowPwdPopup,
    orderInfo,
    cardList,
    selectedCard,
    selectedCardId,
    handlePayError,
    queryPreOrder,
    payPreOrder,
    // toPayVM3DS,
  }
}
